import React, { useState, useMemo, useEffect , useRef} from "react";
import { useNavigate , useLocation} from "react-router-dom";
import { SearchOutlined, EditOutlined , CheckOutlined, } from "@ant-design/icons";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import StyledInput from "src/components/reusable/StyledInput";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import CircularLoader from "src/components/reusable/CircularLoader/CircularLoader";
import StyledTextArea from "src/components/reusable/StyledTextArea";
import StepProgress from "./StepProgress"; 
import "./AIEmailEditor.scss";
import { MoveLeft, MoveRight } from "lucide-react";
import EmailEditor, { EditorRef } from "react-email-editor";
import { fetchCategories, generateTemplate, type GenerateTemplateResult } from "src/services/EngagementAPIs";
import { getApiLangFromI18n } from "src/utils/apiLang";
import { sendCampaignEmail} from "src/services/CampaignAPI";
import LoadingSpinner from "../../../../components/reusable/LoadingSpinner";
import MiniSpinner from "../../../../components/reusable/spinner/spinner";
import EmailStatusPopup from "./EmailStatusPopup";
import ErrorFallback from "src/components/reusable/ErrorFallback/ErrorFallback";
import { formatNumber } from "src/utils/numberFormat";

type CampaignKey =
  | "Customer Lifecycle Campaigns"
  | "Informational & Brand Content"
  | "Promotional Campaigns"
  | "Behavioral Campaigns"
  | "Custom";
interface Campaign {
  value: CampaignKey;
  titleKey: string;
  descKey: string;
}

interface SubCampaign {
  value: string;
  titleKey: string;
  descKey: string;
}

type UnlayerDesign = {
  schemaVersion: number;
  counters: Record<string, number>;
  body: {
    id: string;
    values: Record<string, any>;
    headers: any[];
    footers: any[];
    rows: any[];
  };
};

interface SchedulePreset {
  label: string;
  time: string;
  dayOffset?: number;
}

const AIEmailEditor: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const steps = useMemo(
    () => [
      t("campaignFlow.aiEmailEditor.steps.campaignSetup"),
      t("campaignFlow.aiEmailEditor.steps.selectCategories"),
      t("campaignFlow.aiEmailEditor.steps.chooseCampaignType"),
      t("campaignFlow.aiEmailEditor.steps.aiPromptTone"),
      t("campaignFlow.aiEmailEditor.steps.reviewSend"),
    ],
    [t]
  );
const location = useLocation();
interface CampaignDataState {
  sendTo?: string[];
  companyNameEngagements?: string;
  campaignName?: string;
}
  const [step, setStep] = useState<number>(1);
const [loading, setLoading] = useState(false);
const [, setWebsiteUrl] = useState("");
const [selectedCampaign, setSelectedCampaign] = useState<string>("");
const [selectedSubCampaign, setSelectedSubCampaign] = useState<string>("");
const [customCampaignName, setCustomCampaignName] = useState("");
const [customSubCampaignName, setCustomSubCampaignName] = useState("");
const [templateHTML, setTemplateHTML] = useState<string>("");
const [templateDesign, setTemplateDesign] = useState<UnlayerDesign | null>(null);
const [selectedTones, setSelectedTones] = useState<string[]>([]);
const [showSchedulePopup, setShowSchedulePopup] = useState(false);
const [scheduledDate, setScheduledDate] = useState("");
const [scheduledTime, setScheduledTime] = useState("");
const [prompt, setPrompt] = useState("");
const [persona, setPersona] = useState("");
const [seasonalTag, setSeasonalTag] = useState("");
const [ctaLink, setCtaLink] = useState("");
const [ctaLabel, setCtaLabel] = useState("");
const [categories, setCategories] = useState<string[]>([]);
const [stepHistory, setStepHistory] = useState<number[]>([1]);
const [showSubCategories, setShowSubCategories] = useState<CampaignKey | null>(null);
const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
const [errorMsg, setErrorMsg] = useState("");
const [currentPage, setCurrentPage] = useState(1);
const [searchTerm, setSearchTerm] = useState("");
const [editingField, setEditingField] = useState<string | null>(null);
const [offerPercent, setOfferPercent] = useState("");
const [isSending, setIsSending] = useState(false);
const itemsPerPage = 8;
const emailEditorRef = useRef<EditorRef>(null);
const [, setEditorLoaded] = useState(false);
const [popupType, setPopupType] = useState<"sent" | "scheduled" | null>(null);
const [popupDate, setPopupDate] = useState<string | undefined>(undefined);
const [popupTime, setPopupTime] = useState<string | undefined>(undefined);
const [campaignName, setCampaignName] = useState("");
const [hasCategoriesError, setHasCategoriesError] = useState(false);
const [fromName, setFromName] = useState(() =>
  localStorage.getItem("company_name_engagements") ||
  localStorage.getItem("company_name") ||
  t("campaignFlow.common.defaultCompany")
);
const [fromEmail, setFromEmail] = useState("marketing@datatram.ai");
const [replyTo, setReplyTo] = useState("support@datatram.com");
const [recipients, setRecipients] = useState<string[]>([]);
const [subjectLine, setSubjectLine] = useState(t("campaignFlow.aiEmailEditor.defaultSubjectLine"));
const [isMobile] = useState(window.innerWidth <= 468);
const toneOptions: string[] = [
  t("campaignFlow.aiEmailEditor.tones.friendly"),
  t("campaignFlow.aiEmailEditor.tones.professional"),
  t("campaignFlow.aiEmailEditor.tones.urgent"),
  t("campaignFlow.aiEmailEditor.tones.playful"),
  t("campaignFlow.aiEmailEditor.tones.informative"),
];

function toggleTone(toneName: string) {
  setSelectedTones(prev =>
    prev.includes(toneName)
      ? prev.filter(t => t !== toneName)
      : [...prev, toneName]
  );
}

const campaigns: Campaign[] = [
  {
    value: "Customer Lifecycle Campaigns",
    titleKey: "campaignFlow.aiEmailEditor.campaignTypes.customerLifecycle",
    descKey: "campaignFlow.aiEmailEditor.campaignTypes.chooseCategory",
  },
  {
    value: "Informational & Brand Content",
    titleKey: "campaignFlow.aiEmailEditor.campaignTypes.informationalBrand",
    descKey: "campaignFlow.aiEmailEditor.campaignTypes.chooseCategory",
  },
  {
    value: "Promotional Campaigns",
    titleKey: "campaignFlow.aiEmailEditor.campaignTypes.promotional",
    descKey: "campaignFlow.aiEmailEditor.campaignTypes.chooseCategory",
  },
  {
    value: "Behavioral Campaigns",
    titleKey: "campaignFlow.aiEmailEditor.campaignTypes.behavioral",
    descKey: "campaignFlow.aiEmailEditor.campaignTypes.chooseCategory",
  },
  {
    value: "Custom",
    titleKey: "campaignFlow.aiEmailEditor.campaignTypes.custom",
    descKey: "campaignFlow.aiEmailEditor.campaignTypes.defineOwn",
  },
];

const subCampaigns: Record<CampaignKey, SubCampaign[]> = {
  "Customer Lifecycle Campaigns": [
    {
      value: "Welcome Series",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.welcomeSeries.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.welcomeSeries.desc",
    },
    {
      value: "Abandoned Cart Recovery",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.abandonedCartRecovery.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.abandonedCartRecovery.desc",
    },
    {
      value: "Browse Abandonment",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.browseAbandonment.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.browseAbandonment.desc",
    },
    {
      value: "Post Purchase Follow Up",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.postPurchaseFollowUp.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.postPurchaseFollowUp.desc",
    },
    {
      value: "Behavioral",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.behavioralReminder.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.behavioralReminder.desc",
    },
    {
      value: "Win Back Campaigns",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.winBackCampaigns.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.winBackCampaigns.desc",
    },
  ],
  "Informational & Brand Content": [
    {
      value: "Brand Storytelling Series",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.brandStorytellingSeries.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.brandStorytellingSeries.desc",
    },
    {
      value: "Style Guides / Lookbooks",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.styleGuidesLookbooks.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.styleGuidesLookbooks.desc",
    },
    {
      value: "How To Content",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.howToContent.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.howToContent.desc",
    },
    {
      value: "Newsletter or Product Digest",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.newsletterProductDigest.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.newsletterProductDigest.desc",
    },
  ],
  "Promotional Campaigns": [
    {
      value: "Flash Sale Alerts",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.flashSaleAlerts.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.flashSaleAlerts.desc",
    },
    {
      value: "Seasonal/Holiday Promotions",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.seasonalHolidayPromotions.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.seasonalHolidayPromotions.desc",
    },
    {
      value: "New Product Launches",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.newProductLaunches.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.newProductLaunches.desc",
    },
    {
      value: "Exclusive VIP Offers",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.exclusiveVipOffers.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.exclusiveVipOffers.desc",
    },
    {
      value: "Price Drop Alerts",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.priceDropAlerts.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.priceDropAlerts.desc",
    },
  ],
  "Behavioral Campaigns": [
    {
      value: "Product Recommendation Emails",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.productRecommendationEmails.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.productRecommendationEmails.desc",
    },
    {
      value: "Birthday or Anniversary Emails",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.birthdayOrAnniversaryEmails.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.birthdayOrAnniversaryEmails.desc",
    },
    {
      value: "Review & UGC Requests",
      titleKey: "campaignFlow.aiEmailEditor.subCampaigns.reviewUgcRequests.title",
      descKey: "campaignFlow.aiEmailEditor.subCampaigns.reviewUgcRequests.desc",
    },
  ],
  Custom: [],
};
const handleRecipientChange = (value: string) => {
  const emails = value.split(",").map(email => email.trim());
  setRecipients(emails);
};

const handleQuickSchedule = (preset: SchedulePreset) => {
  const today = new Date();
  const targetDate = new Date(today);
  if (typeof preset.dayOffset === "number") {
    targetDate.setDate(today.getDate() + preset.dayOffset);
  }
  const dateStr = targetDate.toISOString().split("T")[0];
  setScheduledDate(dateStr);
  setScheduledTime(preset.time);
};

const goToStep = (nextStep: number) => {
  setStep(nextStep);
  setStepHistory(prev => [...prev, nextStep]);
};
  useEffect(() => {
  const savedUrl = localStorage.getItem("website_url") || "";
  setWebsiteUrl(savedUrl);
}, []);

const campaignData = (location.state as { campaignData?: CampaignDataState })
  ?.campaignData;

useEffect(() => {
  if (campaignData?.sendTo?.length) {
    setRecipients(campaignData.sendTo);
  }
  if (campaignData?.campaignName) {
    setCampaignName(campaignData.campaignName);
  }
  if (campaignData?.companyNameEngagements) {
    setFromName(campaignData.companyNameEngagements);
    localStorage.setItem(
      "company_name_engagements",
      campaignData.companyNameEngagements
    );
  }
}, [campaignData]);

const formatCategoryName = (cat: string) => {
  let name = cat.split("/").pop() || cat;
  name = name.replace(/-/g, " ");
  name = name.replace(/\b\w/g, char => char.toUpperCase());
  return name;
};

useEffect(() => {
  if (errorMsg) {
    const timer = setTimeout(() => setErrorMsg(""), 4000);
    return () => clearTimeout(timer);
  }
}, [errorMsg]);
useEffect(() => {
  if (step === 1) {
    loadCategories();   
  }
}, [step]);

// Fetch categories
const loadCategories = async () => {
  setLoading(true);
  try {
    const res = await fetchCategories(getApiLangFromI18n(i18n));
    setCategories(res.website_categories || []);
    setHasCategoriesError(false);
  } catch (err) {
    message.error(t("campaignFlow.aiEmailEditor.fetchCategoriesFailed"));
    setHasCategoriesError(true);
  } finally {
    setLoading(false);
  }
};

const handleRetryCategories = () => {
  setHasCategoriesError(false);
  loadCategories();
};
// Generate template
const handleGenerate = async () => {
  setLoading(true);

  const payload = {
    category_links: selectedCategories,
    campaign_type: selectedCampaign,
    campaign_subtype: selectedSubCampaign,
    campaign_name: campaignName,
    prompt,
    tone: selectedTones,
    persona,
    seasonal_tag: seasonalTag,
    cta_label: ctaLabel,
    cta_url: ctaLink,
    lang: getApiLangFromI18n(i18n),
    ...(offerPercent ? { offer: offerPercent } : {}),
  };

  const res: GenerateTemplateResult = await generateTemplate(payload);

  if (res.success) {
    const anyRes: any = res;
    let design: UnlayerDesign | null = null;
    if (anyRes.design) {
      if (typeof anyRes.design === "string") {
        try {
          design = JSON.parse(anyRes.design);
        } catch (error) {
          message.error(t("campaignFlow.aiEmailEditor.parseDesignFailed"));
        }
      } else {
        design = anyRes.design as UnlayerDesign;
      }
    }
    setTemplateDesign(design);
    setTemplateHTML(typeof anyRes.template === "string" ? anyRes.template : "");
    setStep(4);
  } else if (!res.toastShown) {
    message.error(
      res.errorMessage || "Failed to generate template. Please try again.",
    );
  }

  setLoading(false);
};

const handleSendCampaign = async (sendAt?: string) => {
  try {
    setIsSending(true);

    const exportedHtml = await new Promise<string>(resolve => {
      emailEditorRef.current?.editor?.exportHtml((data: any) => {
        resolve(data?.html || "");
      });
    });

    const html = exportedHtml || templateHTML;

    if (!html) {
      throw new Error("Missing campaign HTML");
    }

    const payload = {
      tenant_id: 0,
      campaign_name: campaignName,
      send_to: recipients,
      dont_send_to: [],
      html,
      send_at: sendAt || new Date().toISOString(),
      company_name_engagements: fromName,
      lang: getApiLangFromI18n(i18n),
    };

    await sendCampaignEmail(payload);

    if (sendAt) {
      message.success(t("campaignFlow.aiEmailEditor.campaignScheduled"));
      const scheduledDate = new Date(sendAt);
      const dateStr = popupDate || scheduledDate.toISOString().split("T")[0];
      let timeStr = popupTime;
      if (!timeStr) {
        const localHours = scheduledDate.getHours().toString().padStart(2, "0");
        const localMinutes = scheduledDate.getMinutes().toString().padStart(2, "0");
        timeStr = `${localHours}:${localMinutes}`;
      }
      setPopupDate(dateStr);
      setPopupTime(timeStr);
      setPopupType("scheduled");
    } else {
      message.success(t("campaignFlow.aiEmailEditor.campaignSent"));
      setPopupDate(undefined);
      setPopupTime(undefined);
      setPopupType("sent");
    }
  } catch (err) {
    message.error(t("campaignFlow.aiEmailEditor.sendError"));
  } finally {
    setIsSending(false);
  }
};

const loadTemplateIntoEditor = () => {
  const editor = emailEditorRef.current?.editor;

  if (!editor) {
    return;
  }

  if (templateDesign) {
    editor.loadDesign(templateDesign as any);
    return;
  }

  if (typeof templateHTML === "string" && templateHTML.trim()) {
    (window as any).unlayer?.importHtml(templateHTML);
    return;
  }

  editor.loadBlank({});
};

const handleEditorReady = () => {
  setEditorLoaded(true);
  loadTemplateIntoEditor();
};

useEffect(() => {
  if (step === 4) {
    loadTemplateIntoEditor();
  }
}, [step, templateDesign, templateHTML]);

const filteredCategories = useMemo(() => {
  if (!searchTerm.trim()) {return categories;}
  return categories.filter(cat =>
    cat.toLowerCase().includes(searchTerm.toLowerCase())
  );
}, [categories, searchTerm]);

const paginatedCategories = useMemo(() => {
  const start = (currentPage - 1) * itemsPerPage;
  return filteredCategories.slice(start, start + itemsPerPage);
}, [filteredCategories, currentPage, itemsPerPage]);

const totalPages = useMemo(() => {
  return Math.ceil(filteredCategories.length / itemsPerPage);
}, [filteredCategories.length, itemsPerPage]);

const renderField = (
  label: string,
  value: string,
  onChange: (v: string) => void,
  type: "text" | "email" = "text",
  fieldName: string
) => (
  <div className="summary-item">
    <label>{label}</label>
    <div className="summary-input-wrapper">
      <div className="input-with-icon-container">
        <StyledTextInput
          type={type}
          value={value}
          onChange={e => onChange(e.target.value)}
          className="form-input-summary"
          readOnly={fieldName === "fromEmail" || editingField !== fieldName}
        />
        {fieldName !== "fromEmail" &&
          (editingField === fieldName ? (
            <CheckOutlined
              className="edit-icon inside-input"
              onClick={() => setEditingField(null)}
            />
          ) : (
            <EditOutlined
              className="edit-icon inside-input"
              onClick={() => setEditingField(fieldName)}
            />
          ))}
      </div>
    </div>
  </div>
);


const stepnext = () => {
  if (step === 2) {
    if (selectedCategories.includes("Custom")) {
      goToStep(4);
      return;
    }

    if (selectedCategories.length > 0) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
        goToStep(4);
      }, 3000); 
      return;
    }
  }

  goToStep(step + 1);
};


if (loading && step === 2) {
  return <LoadingSpinner />;
}

if (hasCategoriesError) {
  return <ErrorFallback onRetry={handleRetryCategories} />;
}

  return (
    <div className="ai-email-campaign-container">
  {errorMsg && (
  <div className="ai-error-toast">
    <span className="error-icon">âœ–</span>
    {errorMsg}
  </div>
)}
      <div className="ai-email-campaign-setup">
        <div className="page-header">
         <button
           className="back-button back-button--text button button--primary"
           onClick={() => {
             if (stepHistory.length > 1) {
               const newHistory = [...stepHistory];
               newHistory.pop(); 
               const prevStep = newHistory[newHistory.length - 1];
               setStep(prevStep);
               setStepHistory(newHistory);
             } else {
               navigate(-1); 
             }
           }}
          >
            {t("campaignFlow.common.back")}
          </button>

          <span className="Page-header-text">
            {t("campaignFlow.common.breadcrumb")}
          </span>
        </div>
<h2 className="form-title">
  {step === 4
    ? t("campaignFlow.aiEmailEditor.reviewAndSendTitle")
    : t("campaignFlow.common.aiGeneratorTitle")}
</h2>
        <StepProgress current={step} total={4} />
{step === 4 && (
  <h2 className="campaign-summary-heading">{t("campaignFlow.common.campaignSummary")}</h2>
)}


{step === 1 && (
  <div className="categories-step">
    <div className="categories-header-grid">
      <div className="categories-header-text">
        <h3 className="categories-title">{t("campaignFlow.aiEmailEditor.productCategories")}</h3>
        <p className="categories-subtitle">
          {t("campaignFlow.aiEmailEditor.categoriesSubtitle")}
        </p>
      </div>
      <label className="select-all">
        <input
          type="checkbox"
          checked={selectedCategories.length === categories.length}
          onChange={e => {
            if (e.target.checked) {
              setSelectedCategories(categories);
            } else {
              setSelectedCategories([]);
            }
          }}
        />
        {t("campaignFlow.aiEmailEditor.selectAll")}
      </label>
    </div>

    <div className="categories-search">
      <StyledInput
        className="categories-search-input"
        placeholder={t("campaignFlow.aiEmailEditor.searchCategories")}
        value={searchTerm}
        onChange={e => {
          setSearchTerm(e.target.value);
          setCurrentPage(1);
        }}
      />
    </div>
{loading && <MiniSpinner />}
    <div className="categories-grid">
      {paginatedCategories?.map((cat, idx) => {
        const isSelected = selectedCategories.includes(cat);
        return (
          <div
            key={idx}
            className={`category-item ${isSelected ? "selected" : ""}`}
            onClick={() => {
              if (isSelected) {
                setSelectedCategories(
                  selectedCategories.filter(c => c !== cat)
                );
              } else {
                setSelectedCategories([...selectedCategories, cat]);
              }
            }}
          >
            {formatCategoryName(cat)}
          </div>
        );
      })}
    </div>

    {totalPages > 1 && (
      <div className="categories-pagination">
        <button
          className="categories-page-btn"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(p => p - 1)}
        >
        <MoveLeft size={18} />
        </button>
        <span className="categories-page-info">
          {t("campaignFlow.aiEmailEditor.pagesOf", {
            currentPage: formatNumber(currentPage, {}, i18n.language),
            totalPages: formatNumber(totalPages, {}, i18n.language),
          })}
        </span>
        <button
          className="categories-page-btn"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(p => p + 1)}
        >
        <MoveRight size={18} />
        </button>
      </div>
    )}
  </div>
)}

{step === 2 && (
  <div className="campaign-type-step">
    {!showSubCategories ? (
      <>
      <div className="categories-header-text">
        <h3 className="campaign-type-title">{t("campaignFlow.aiEmailEditor.campaignCategory")}</h3>
        <p className="categories-subtitle">
        {t("campaignFlow.aiEmailEditor.campaignCategorySubtitle")}
        </p>
        </div>
        <div className="campaign-type-grid">
          {campaigns?.map((type, idx, arr) => {
            const isLast = idx === arr.length - 1;
            return (
              <div
                key={idx}
                className={`campaign-card ${
                  selectedCampaign === type.value ? "selected" : ""
                } ${isLast ? "center-card" : ""}`}
                onClick={() => {
                if (type.value === "Custom") {
    setShowSubCategories("Custom"); 
  } else {
    setSelectedCampaign(type.value); 
    setShowSubCategories(type.value);
  }
                }}
              >
                <div className="card-title">{t(type.titleKey)}</div>
              </div>
            );
          })}
        </div>
      </>
    ) : (
      <>
        {showSubCategories === "Custom" ? (
          <>
             <div className="categories-header-text">
        <h3 className="campaign-type-title">{t("campaignFlow.aiEmailEditor.customCampaign")}</h3>
        <p className="categories-subtitle">
       {t("campaignFlow.aiEmailEditor.customCampaignSubtitle")}
        </p>
        </div>
            <div className="custom-campaign-form">
              <label className="custom-label">{t("campaignFlow.common.campaignName")}</label>
              <StyledTextInput
                placeholder={t("campaignFlow.common.campaignName")}
                value={customCampaignName}
                onChange={e => setCustomCampaignName(e.target.value)}
              />

              <label className="custom-label">{t("campaignFlow.aiEmailEditor.subCampaignName")}</label>
              <StyledTextInput
                placeholder={t("campaignFlow.aiEmailEditor.subCampaignName")}
                value={customSubCampaignName}
                onChange={e => setCustomSubCampaignName(e.target.value)}
              />
            </div>
          </>
        ) : (
          <>
            <div className="categories-header-text">
        <h3 className="campaign-type-title">{t("campaignFlow.aiEmailEditor.campaignSubCategories")}</h3>
        <p className="categories-subtitle">
        {t("campaignFlow.aiEmailEditor.campaignSubCategoriesSubtitle")}
        </p>
        </div>
            <div className="campaign-type-grid">
              {subCampaigns[showSubCategories]?.map((sub, idx, arr) => {
                const isLast = idx === arr.length - 1;
                const shouldCenter = arr.length % 2 !== 0 && isLast;
                return (
                  <div
                    key={idx}
                    className={`campaign-card ${
                       selectedSubCampaign === sub.value ? "selected" : ""
                     } ${shouldCenter ? "center-card" : ""}`}
                    onClick={() => setSelectedSubCampaign(sub.value)}
                  >
                    <div className="card-title">{t(sub.titleKey)}</div>
                    <div className="card-desc">{t(sub.descKey)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </>
    )}
  </div>
)}

{step === 3 && (
   <div className="ai-prompt-step">
        <h3 className="prompt-title">{t("campaignFlow.aiEmailEditor.steps.aiPromptTone")}</h3>
        <p className="prompt-subtitle">
          {t("campaignFlow.aiEmailEditor.describeCampaignGoal")} <span className="required-star">*</span>
        </p>

        <textarea
          className="prompt-textarea"
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          placeholder={t("campaignFlow.aiEmailEditor.promptPlaceholder")}
        />

        <div className="tone-section">
          <h4 className="tone-title">{t("campaignFlow.aiEmailEditor.tonePresets")} <span className="required-star">*</span></h4>
          <div className="tone-buttons">
            {toneOptions.map(toneName => (
              <button
                key={toneName}
                type="button"
                className={`tone-btn ${selectedTones.includes(toneName) ? "active" : ""}`}
                onClick={() => toggleTone(toneName)}
              >
                {toneName}
              </button>
            ))}
          </div>
        </div>

        <div className="form-section">
          <label className="form-label">{t("campaignFlow.aiEmailEditor.audiencePersona")} <span className="required-star">*</span></label>
          <StyledTextInput
            type="text"
            className="prompt-input"
            value={persona}
            onChange={e => setPersona(e.target.value)}
            placeholder={t("campaignFlow.aiEmailEditor.audiencePersonaPlaceholder")}
          />
        </div>

        <div className="form-section">
          <label className="form-label">{t("campaignFlow.aiEmailEditor.seasonalTag")} <span className="required-star">*</span></label>
          <StyledTextInput
            type="text"
            className="prompt-input"
            value={seasonalTag}
            onChange={e => setSeasonalTag(e.target.value)}
            placeholder={t("campaignFlow.aiEmailEditor.seasonalTagPlaceholder")}
          />
        </div>

        <div className="form-section">
          <div className="cta-row">
            {isMobile ? (
              <>
                <div className="cta-labels-row">
                  <label className="form-label">{t("campaignFlow.aiEmailEditor.offerPercent")}</label>
                </div>
                <div className="cta-inputs-row">
                  <StyledTextInput
                    type="text"
                    className="cta-input-offer"
                    value={offerPercent}
                    onChange={e => setOfferPercent(e.target.value)}
                    placeholder={t("campaignFlow.aiEmailEditor.offerPercentPlaceholder")}
                  />
                </div>

                <div className="cta-labels-row">
                  <label className="form-label">{t("campaignFlow.aiEmailEditor.ctaUrl")} <span className="required-star">*</span></label>
                </div>
                <div className="cta-inputs-row">
                  <StyledTextInput
                    type="text"
                    className="cta-input-link"
                    value={ctaLink}
                    onChange={e => setCtaLink(e.target.value)}
                    placeholder={t("campaignFlow.aiEmailEditor.ctaUrlPlaceholder")}
                  />
                </div>

                <div className="cta-labels-row">
                  <label className="form-label-cta">{t("campaignFlow.aiEmailEditor.ctaLabel")} <span className="required-star">*</span></label>
                </div>
                <div className="cta-inputs-row">
                  <StyledTextInput
                    type="text"
                    className="cta-input-label"
                    value={ctaLabel}
                    onChange={e => setCtaLabel(e.target.value)}
                    placeholder={t("campaignFlow.aiEmailEditor.shopNow")}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="cta-labels-row">
                  <label className="form-label">{t("campaignFlow.aiEmailEditor.offerPercent")}</label>
                  <label className="form-label">{t("campaignFlow.aiEmailEditor.ctaUrl")} <span className="required-star">*</span></label>
                  <label className="form-label-cta">{t("campaignFlow.aiEmailEditor.ctaLabel")} <span className="required-star">*</span></label>
                </div>
                <div className="cta-inputs-row">
                  <StyledTextInput
                    type="text"
                    className="cta-input-offer"
                    value={offerPercent}
                    onChange={e => setOfferPercent(e.target.value)}
                    placeholder={t("campaignFlow.aiEmailEditor.offerPercentPlaceholder")}
                  />
                  <StyledTextInput
                    type="text"
                    className="cta-input-link"
                    value={ctaLink}
                    onChange={e => setCtaLink(e.target.value)}
                    placeholder={t("campaignFlow.aiEmailEditor.ctaUrlPlaceholder")}
                  />
                  <StyledTextInput
                    type="text"
                    className="cta-input-label"
                    value={ctaLabel}
                    onChange={e => setCtaLabel(e.target.value)}
                    placeholder={t("campaignFlow.aiEmailEditor.shopNow")}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="generate-actions">
          <button className="button button--primary generate-btn" onClick={handleGenerate} disabled={loading}>
            {t("campaignFlow.aiEmailEditor.generateWithAI")}
          </button>
          {loading && (
            <span className="generate-spinner">
              <CircularLoader size={22} thickness={3} />
            </span>
          )}
        </div>

      </div>
)}
{step === 4 && (
  <div className="review-step">
    <div className="campaign-summary-box">
     <div className="summary-grid">
  {renderField(t("campaignFlow.common.campaignName"), campaignName, setCampaignName, "text", "campaignName")}
  {renderField(t("campaignFlow.common.replyTo"), replyTo, setReplyTo, "email", "replyTo")}
  {renderField(t("campaignFlow.common.recipients"), recipients.join(", "), handleRecipientChange, "text", "recipients")}
  {renderField(t("campaignFlow.common.subjectLine"), subjectLine, setSubjectLine, "text", "subjectLine")}
  {renderField(t("campaignFlow.common.fromName"), fromName, setFromName, "text", "fromName")}
  {renderField(t("campaignFlow.common.fromEmail"), fromEmail, setFromEmail, "email", "fromEmail")}
</div>

      <div className="email-editor-container">
        <EmailEditor
          ref={emailEditorRef}
          onLoad={handleEditorReady}
          options={{ displayMode: "email" }}
        />
      </div>
    </div>

    <div className="Email-footer-actions">
      <button
        className="campaign-next-btn"
        onClick={() => setShowSchedulePopup(true)}
      >
        {t("campaignFlow.common.scheduleSendUpper")}
      </button>

     <div className="send-now-actions">
     <button
  className="campaign-next-btn"
  onClick={async () => {
    await handleSendCampaign(); 
  }}
  disabled={isSending}
>
  {isSending ? t("campaignFlow.common.sending") : t("campaignFlow.common.sendNowUpper")}
</button>
      {isSending && (
        <span className="send-now-spinner">
          <CircularLoader size={22} thickness={3} />
        </span>
      )}
     </div>
    </div>

    {popupType && (
  <EmailStatusPopup
    type={popupType} 
    onClose={() => {
      setPopupType(null);
      setPopupDate(undefined);
      setPopupTime(undefined);
    }}
    date={popupDate}
    time={popupTime}
  />
)}

    {showSchedulePopup && (
      <div className="schedule-modal-overlay">
        <div className="schedule-modal">
          <h4 className="schedule-picker-title">{t("campaignFlow.common.scheduleCampaign")}</h4>
          <p className="schedule-picker-paragraph">{t("campaignFlow.common.pakistanStandardTime")}</p>

          <div className="schedule-picker-presets">
            {[
              { label: t("campaignFlow.common.tomorrowMorning"), time: "09:00", dayOffset: 1 },
              { label: t("campaignFlow.common.tomorrowEvening"), time: "18:00", dayOffset: 1 },
              {
                label: t("campaignFlow.common.nextMondayMorning"),
                time: "09:00",
                dayOffset: (8 - new Date().getDay()) % 7 || 7,
              },
            ].map((preset, index) => (
              <button
                key={index}
                className="preset-button"
                onClick={() => handleQuickSchedule(preset)} 
              >
                {preset.label}
              </button>
            ))}
          </div>

          <label className="schedule-label">{t("campaignFlow.common.selectDate")}</label>
          <input
            type="date"
            className="schedule-input"
            min={new Date().toISOString().split("T")[0]}
            value={scheduledDate}
            onChange={e => setScheduledDate(e.target.value)}
          />

          <label className="schedule-label">{t("campaignFlow.common.selectTime")}</label>
          <input
            type="time"
            className="schedule-input"
            value={scheduledTime}
            onChange={e => setScheduledTime(e.target.value)}
          />

          <div className="schedule-actions">
            <button
  className="button button--primary proceed-button"
  onClick={async () => {
    if (!scheduledDate || !scheduledTime) {return;}
    const scheduleDateTime = new Date(`${scheduledDate}T${scheduledTime}:00`).toISOString();
    setPopupDate(scheduledDate);
    setPopupTime(scheduledTime);
    await handleSendCampaign(scheduleDateTime);
    setShowSchedulePopup(false); 
  }}
  disabled={!scheduledDate || !scheduledTime || isSending}>
  {isSending ? t("campaignFlow.common.scheduling") : t("campaignFlow.common.schedule")}
</button>

            <button
              className="button button--primary proceed-button"
              onClick={() => setShowSchedulePopup(false)} >
              {t("campaignFlow.common.cancel")}
            </button>
          </div>
        </div>
      </div>
    )}

  </div>
)}

{/* Navigation buttons */}
<div
  className={`step-nav ${
    step === 1 ? "step-nav--categories" : step === 2 ? "step-nav--campaign" : ""
  } ${step === 2 && showSubCategories ? "step-nav--with-back" : ""}`}
>
  {step === 2 ? (
    !showSubCategories ? (
      <button
        onClick={() => {
          if (showSubCategories === "Custom") {
            setSelectedCampaign(customCampaignName);
            setSelectedSubCampaign(customSubCampaignName);
          }
          goToStep(3);
        }}
        disabled={
          showSubCategories === "Custom"
            ? !(customCampaignName && customSubCampaignName)
            : !selectedSubCampaign
        }
        className="button button--primary ai-next-btn"
      >
        {t("campaignFlow.common.continue")}
      </button>
    ) : (
      <>
        <button
          onClick={() => {
            setShowSubCategories(null);
            setSelectedCategories([]);
          }}
          className="button button--primary ai-back-btn"
        >
          {t("campaignFlow.common.back")}
        </button>

        <button
          onClick={() => {
            goToStep(3);
          }}
          disabled={
            showSubCategories === "Custom"
              ? !(customCampaignName && customSubCampaignName)
              : !selectedSubCampaign
          }
          className="button button--primary ai-next-btn"
        >
          {t("campaignFlow.common.continue")}
        </button>
      </>
    )
  ) : step === 1 ? (
    <button
      onClick={() => {
        if (selectedCategories.length > 0) {
          goToStep(2);
        }
      }}
      disabled={selectedCategories.length === 0 || loading}
      className="button button--primary ai-next-btn"
    >
      {loading ? t("campaignFlow.aiEmailEditor.fetching") : t("campaignFlow.common.continue")}
    </button>
  ) : (
    step !== 3 &&
    step !== 4 && (
      <button
        onClick={stepnext}
        disabled={step === steps.length}
        className="button button--primary ai-next-btn"
      >
        {step === steps.length ? t("campaignFlow.common.finish") : t("campaignFlow.common.continue")}
      </button>
    )
  )}
</div>
      </div>
 
    </div>

  );
};

export default AIEmailEditor;


